import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  ArrowRightLeft, Pointer, BadgeCheck, Phone, Delete, ArrowRight, QrCode, 
  XCircle, CheckCircle2, Clock, MapPin, User, Sparkles, CalendarPlus, 
  Users, Hourglass, UserPlus, List, RefreshCw, ClipboardList, AlertCircle 
} from 'lucide-react';

// --- MOCK DATA ---
const mockDatabase = {
  '0909506018': {
    name: 'Nguyễn Khách Hàng', phone: '0909506018', email: 'khachhang09@email.com',
    address: 'Quận 10, TP.HCM', service: 'Nâng cơ Hifu', price: '15,000,000đ',
    branch: 'Khothi Quận 1', type: 'booked'
  },
  '0901234567': {
    name: 'Nguyễn Bích Ngọc', phone: '0901234567', email: 'ngocnb@email.com',
    address: 'Quận 1, TP.HCM', service: 'Trẻ hóa da Thermage FLX', price: '45,000,000đ',
    branch: 'Khothi Quận 1', type: 'booked'
  },
  '0911222333': {
    name: 'Trần Thị Hà', phone: '0911222333', email: 'hantt@email.com',
    address: 'Quận 3, TP.HCM', service: 'Tắm trắng phi thuyền', price: '5,000,000đ',
    branch: 'Khothi Quận 1', type: 'booked'
  }
};

const customerHistory = {
  '0988111222': { name: 'Lê Hoàng Anh' },
  '0999888777': { name: 'Phan Thị Yến' }
};

const availableServices = [
  { id: 's1', name: 'Tư vấn tổng quát', price: 0, priceText: 'Miễn phí' },
  { id: 's2', name: 'Trẻ hóa da Thermage FLX', price: 45000000, priceText: '45,000,000đ' },
  { id: 's3', name: 'Chăm sóc da chuyên sâu', price: 25000000, priceText: '2,500,000đ' },
  { id: 's4', name: 'Tắm trắng phi thuyền', price: 5000000, priceText: '5,000,000đ' },
  { id: 's5', name: 'Nâng cơ Hifu', price: 15000000, priceText: '15,000,000đ' },
  { id: 's6', name: 'Phun xăm thẩm mỹ', price: 3000000, priceText: '3,000,000đ' }
];

const initialQueue = [
  { id: 1, time: '08:15', name: 'Lê Hoàng Anh', phone: '0988111222', service: 'Chăm sóc da chuyên sâu', type: 'walkin', status: 'served' },
  { id: 2, time: '08:30', name: 'Trần Thị Hà', phone: '0911222333', service: 'Tắm trắng phi thuyền', type: 'booked', status: 'waiting' }
];

const IDLE_TIME = 45000; // 45s timeout

const CustomStyles = () => (
  <style>{`
    .bg-spa-dark { background-color: #092C23; }
    .text-spa-dark { color: #092C23; }
    .bg-spa-gold { background-color: #C79F4B; }
    .text-spa-gold { color: #C79F4B; }
    .border-spa-gold { border-color: #C79F4B; }
    
    .fade-in { animation: fadeIn 0.4s ease-out forwards; }
    @keyframes fadeIn {
        from { opacity: 0; transform: translateY(10px); }
        to { opacity: 1; transform: translateY(0); }
    }

    @keyframes slideInRight {
        from { transform: translateX(100%); opacity: 0; }
        to { transform: translateX(0); opacity: 1; }
    }
    .animate-toast { animation: slideInRight 0.3s ease-out forwards; }

    @keyframes ripple {
        0% { transform: scale(1); opacity: 0.8; }
        100% { transform: scale(2.5); opacity: 0; }
    }
    .animate-ripple { animation: ripple 3s infinite; }
    .animate-ripple-delayed { animation: ripple 3s infinite 1.5s; }

    @keyframes subtleFloat {
        0%, 100% { transform: translateY(0); }
        50% { transform: translateY(-5px); }
    }
    .animate-float { animation: subtleFloat 4s ease-in-out infinite; }

    ::-webkit-scrollbar { width: 6px; height: 6px; }
    ::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }
  `}</style>
);

const getCurrentTime = () => {
  const now = new Date();
  return `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
};

// ĐÃ SỬA: Tách ClockDisplay ra ngoài Component chính
const ClockDisplay = () => {
  const [time, setTime] = useState(new Date());
  useEffect(() => {
    const timerId = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timerId);
  }, []);
  return (
    <>
      <div className="text-5xl font-light tracking-widest text-white drop-shadow-md">
        {time.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })}
      </div>
      <div className="text-xs uppercase tracking-[0.25em] mt-3 text-[#FDE08B] font-medium drop-shadow-sm">
        {time.toLocaleDateString('vi-VN', { weekday: 'long', day: '2-digit', month: 'long' })}
      </div>
    </>
  );
};

export default function KhothiCheckinSystem() {
  const [currentView, setCurrentView] = useState('client');
  const [clientScreen, setClientScreen] = useState('idle');
  const [queueData, setQueueData] = useState(initialQueue);
  const [toasts, setToasts] = useState([]);
  const idleTimerRef = useRef(null);

  // ĐÃ SỬA: Đưa các state của việc đăng ký ra Component ngoài cùng để không bị reset
  const [phone, setPhone] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [successData, setSuccessData] = useState(null);
  const [regData, setRegData] = useState({ name: '', branch: 'Khothi Quận 1', selectedServices: [] });

  // --- HỆ THỐNG TOAST MESSAGES ---
  const showToast = useCallback((message, type = 'error') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 3000);
  }, []);

  // --- QUẢN LÝ THỜI GIAN CHỜ (IDLE TIMER) ---
  const resetIdleTimer = useCallback(() => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    
    if (currentView === 'client' && clientScreen !== 'idle' && clientScreen !== 'success') {
      idleTimerRef.current = setTimeout(() => {
        setClientScreen('idle');
      }, IDLE_TIME);
    }
  }, [currentView, clientScreen]);

  useEffect(() => {
    const events = ['touchstart', 'mousemove', 'keypress', 'click', 'scroll'];
    const handleActivity = () => resetIdleTimer();
    events.forEach(e => window.addEventListener(e, handleActivity));
    return () => events.forEach(e => window.removeEventListener(e, handleActivity));
  }, [resetIdleTimer]);

  useEffect(() => { resetIdleTimer(); }, [clientScreen, currentView, resetIdleTimer]);

  // --- HÀNH ĐỘNG CHECK-IN ---
  const handleAddToQueue = (customerData) => {
    const exists = queueData.find(item => item.phone === customerData.phone && item.status === 'waiting');
    if (exists) return;

    const newItem = {
      id: Date.now(),
      time: getCurrentTime(),
      name: customerData.name,
      phone: customerData.phone,
      service: customerData.service,
      type: customerData.type,
      status: 'waiting'
    };
    
    setQueueData(prev => [newItem, ...prev]);
    if (currentView !== 'admin') {
      showToast(`Khách hàng ${customerData.name} vừa check-in!`, 'success');
    }
  };

  // ĐÃ SỬA: Biến thành một hàm render thông thường thay vì một React Component lồng nhau
  const renderClientView = () => {
    // Numpad logic
    const appendNumber = (num) => { if (phone.length < 11) setPhone(p => p + num); };
    const deleteNumber = () => setPhone(p => p.slice(0, -1));

    // Submit logic
    const processCheckin = (phoneToProcess = phone) => {
      const cleanPhone = phoneToProcess.trim();
      if (!cleanPhone || cleanPhone.length < 9) {
        showToast('Vui lòng nhập số điện thoại hợp lệ.');
        return;
      }
      if (mockDatabase[cleanPhone]) {
        finishCheckin(mockDatabase[cleanPhone]);
      } else {
        const pastName = customerHistory[cleanPhone]?.name || '';
        setRegData(prev => ({ ...prev, name: pastName, selectedServices: [] }));
        setShowModal(true);
      }
    };

    const confirmRegister = () => {
      setShowModal(false);
      setClientScreen('register');
    };

    const submitRegistration = () => {
      if (!regData.name.trim()) return showToast('Vui lòng nhập họ và tên của Quý khách.');
      if (regData.selectedServices.length === 0) return showToast('Vui lòng chọn ít nhất 1 dịch vụ quan tâm.');

      const selectedDocs = availableServices.filter(s => regData.selectedServices.includes(s.id));
      const finalServiceNames = selectedDocs.map(s => s.name).join(', ');
      const totalPrice = selectedDocs.reduce((sum, s) => sum + s.price, 0);
      const finalPriceString = totalPrice === 0 ? 'Miễn phí' 
        : new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(totalPrice);

      finishCheckin({
        name: regData.name.trim(), phone: phone, email: 'Chưa cập nhật', address: 'Chưa cập nhật',
        service: finalServiceNames, price: finalPriceString, branch: regData.branch, type: 'walkin'
      });
    };

    const finishCheckin = (data) => {
      setSuccessData({ ...data, time: getCurrentTime() });
      setClientScreen('success');
      handleAddToQueue(data);
      
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => {
        setPhone('');
        setClientScreen('idle');
      }, 10000);
    };

    return (
      <div className="flex-1 flex items-center justify-center p-4 relative w-full min-h-screen bg-[url('https://vienthammykhothi.vn/uploads/2025/11/photo_library/C4xgoSiQ876b912cca89206e784b255cad32861d7e.jpg')] bg-cover bg-center">
        <div className="absolute inset-0 bg-[#092C23]/40 backdrop-blur-[2px]"></div>

        <div className="relative z-10 w-full max-w-md rounded-2xl shadow-2xl overflow-hidden fade-in">
          {clientScreen !== 'idle' && (
            <div className="bg-spa-dark px-6 py-6 text-center relative overflow-hidden flex flex-col items-center justify-center">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#C79F4B] via-[#FDE08B] to-[#C79F4B]"></div>
              <img src="https://khothi-cms.buso.asia/images/logo-v.png" alt="Khothi" className="h-14 object-contain drop-shadow-md rounded-[5px]" />
            </div>
          )}

          {/* MÀN HÌNH: CHỜ (IDLE) */}
          {clientScreen === 'idle' && (
            <div className="bg-[#092C23]/80 backdrop-blur-md p-8 md:p-12 flex flex-col items-center justify-between cursor-pointer min-h-[550px] relative overflow-hidden" 
                 onClick={() => { setPhone(''); setClientScreen('welcome'); }}>
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-[#C79F4B]/10 via-transparent to-transparent pointer-events-none"></div>
              <div className="w-full flex flex-col items-center justify-center z-20 mt-2">
                <ClockDisplay />
                <div className="w-10 h-[1px] bg-[#C79F4B]/40 mt-6"></div>
              </div>
              <div className="flex flex-col items-center justify-center z-20 flex-1 my-8">
                <img src="https://khothi-cms.buso.asia/images/logo-v.png" alt="Logo" className="h-24 md:h-28 object-contain drop-shadow-2xl mb-8 animate-float rounded-[5px]" />
                <h2 className="text-white/95 text-lg md:text-xl font-light tracking-[0.2em] uppercase text-center drop-shadow-md">Kính chào Quý khách</h2>
                <p className="text-[#FDE08B]/80 text-sm font-light mt-3 italic tracking-wide">Trải nghiệm đẳng cấp 5 sao</p>
              </div>
              <div className="flex flex-col items-center justify-center z-20 mb-2">
                <div className="relative flex items-center justify-center w-24 h-24 mb-6">
                  <div className="absolute w-full h-full border-[1.5px] border-[#C79F4B]/40 rounded-full animate-ripple"></div>
                  <div className="absolute w-full h-full border-[1.5px] border-[#C79F4B]/20 rounded-full animate-ripple-delayed"></div>
                  <div className="w-16 h-16 bg-white/5 backdrop-blur-sm border border-[#C79F4B]/50 rounded-full flex items-center justify-center text-[#FDE08B] transition-all hover:bg-[#C79F4B]/20 hover:scale-105 shadow-[0_0_15px_rgba(199,159,75,0.15)]">
                    <Pointer size={32} strokeWidth={1.5} />
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="h-[1px] w-10 md:w-12 bg-gradient-to-r from-transparent to-[#C79F4B]/60"></div>
                  <span className="text-xs md:text-sm text-white/90 font-light tracking-[0.15em] uppercase">Chạm để bắt đầu</span>
                  <div className="h-[1px] w-10 md:w-12 bg-gradient-to-l from-transparent to-[#C79F4B]/60"></div>
                </div>
              </div>
            </div>
          )}

          {/* MÀN HÌNH: NHẬP SĐT */}
          {clientScreen === 'welcome' && (
            <div className="bg-white/95 backdrop-blur-md p-6 md:p-8 fade-in min-h-[450px]">
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-slate-100 rounded-full mb-3 text-spa-dark">
                  <BadgeCheck size={24} />
                </div>
                <h2 className="text-gray-800 text-lg font-semibold">Chào mừng Quý khách</h2>
                <p className="text-gray-500 text-sm mt-1">Vui lòng check-in để chuyên viên hỗ trợ</p>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Số điện thoại</label>
                  <div className="relative">
                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                    <input type="tel" readOnly value={phone} placeholder="Nhập số điện thoại..." className="w-full pl-12 pr-4 py-3 md:py-4 border border-gray-200 rounded-xl text-lg md:text-xl text-center font-bold tracking-[0.2em] focus:outline-none focus:border-spa-gold focus:ring-1 focus:ring-spa-gold bg-gray-50 text-spa-dark cursor-default shadow-inner" />
                  </div>
                </div>
                
                <div className="grid grid-cols-3 gap-2 md:gap-3 py-2">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => (
                    <button key={num} onClick={() => appendNumber(num.toString())} className="h-12 md:h-14 bg-white border border-gray-100 rounded-xl text-xl font-medium text-gray-700 shadow-sm hover:bg-gray-50 active:bg-gray-200 active:scale-95 transition-all">{num}</button>
                  ))}
                  <button onClick={deleteNumber} className="h-12 md:h-14 bg-red-50 border border-red-100 rounded-xl text-xl font-medium text-red-500 shadow-sm hover:bg-red-100 active:bg-red-200 active:scale-95 transition-all flex items-center justify-center">
                    <Delete size={24} />
                  </button>
                  <button onClick={() => appendNumber('0')} className="h-12 md:h-14 bg-white border border-gray-100 rounded-xl text-xl font-medium text-gray-700 shadow-sm hover:bg-gray-50 active:bg-gray-200 active:scale-95 transition-all">0</button>
                  <button onClick={() => processCheckin()} className="h-12 md:h-14 bg-spa-dark border border-spa-dark rounded-xl text-white shadow-md hover:bg-slate-800 active:scale-95 transition-all flex items-center justify-center">
                    <ArrowRight size={28} />
                  </button>
                </div>

                <div className="flex flex-col pt-1">
                  <div className="relative flex items-center py-2">
                    <div className="flex-grow border-t border-gray-200"></div>
                    <span className="flex-shrink-0 mx-4 text-gray-400 text-xs uppercase">Hoặc</span>
                    <div className="flex-grow border-t border-gray-200"></div>
                  </div>
                  <button onClick={() => setClientScreen('scanner')} className="w-full bg-white text-spa-dark border border-gray-200 py-3 rounded-xl text-sm font-medium hover:bg-gray-50 transition-all flex items-center justify-center gap-2 mt-2">
                    <QrCode size={20} /> Quét mã QR Code
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* MÀN HÌNH: QUÉT QR */}
          {clientScreen === 'scanner' && (
            <div className="bg-white/95 backdrop-blur-md p-6 md:p-8 fade-in min-h-[450px] flex flex-col">
              <div className="text-center mb-6">
                <h2 className="text-gray-800 text-lg font-semibold">Quét mã QR</h2>
                <p className="text-gray-500 text-sm mt-1">Đưa mã QR của bạn vào khung hình</p>
              </div>
              <div className="flex-1 flex flex-col justify-center">
                <div className="bg-black rounded-xl overflow-hidden mb-4 relative flex flex-col items-center justify-center border-4 border-gray-800" style={{ minHeight: '250px' }}>
                  <div className="w-48 h-48 border-2 border-white/50 rounded-lg relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-full h-1 bg-green-500 shadow-[0_0_10px_#22c55e] animate-[subtleFloat_2s_linear_infinite]"></div>
                    <QrCode size={120} className="text-white/20 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
                  </div>
                  <p className="text-white/60 text-xs mt-4">Camera đang quét...</p>
                  
                  <button onClick={() => {
                    showToast("Quét mã QR thành công!", "success");
                    setPhone('0901234567'); 
                    setClientScreen('welcome');
                    setTimeout(() => processCheckin('0901234567'), 500);
                  }} className="absolute bottom-2 bg-white/20 hover:bg-white/30 text-white text-xs px-3 py-1 rounded-full backdrop-blur-sm">
                    (Giả lập: Quét thử mã)
                  </button>
                </div>
                <button onClick={() => setClientScreen('welcome')} className="w-full bg-gray-100 text-gray-700 py-3 rounded-xl text-sm font-medium hover:bg-gray-200 transition-all flex items-center justify-center gap-2">
                  <XCircle size={20} /> Hủy quét mã
                </button>
              </div>
            </div>
          )}

          {/* MÀN HÌNH: ĐĂNG KÝ (Khách mới) */}
          {clientScreen === 'register' && (
            <div className="bg-white/95 backdrop-blur-md p-6 md:p-8 fade-in min-h-[450px]">
              <div className="text-center mb-6">
                <h2 className="text-gray-800 text-lg font-semibold">Tạo lịch hẹn nhanh</h2>
                <p className="text-gray-500 text-sm mt-1">Chưa tìm thấy lịch hẹn. Quý khách vui lòng điền thông tin</p>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Số điện thoại</label>
                    <input type="text" readOnly value={phone} className="w-full px-4 py-3 bg-gray-100 border border-gray-200 rounded-xl text-sm text-gray-500 font-medium cursor-not-allowed" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Chi nhánh</label>
                    <select value={regData.branch} onChange={(e) => setRegData({...regData, branch: e.target.value})} className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-spa-gold focus:ring-1 focus:ring-spa-gold bg-white">
                      <option value="Khothi Quận 1">Khothi Quận 1</option>
                      <option value="Khothi Quận 3">Khothi Quận 3</option>
                      <option value="Khothi Quận 10">Khothi Quận 10</option>
                    </select>
                  </div>
                </div>
                
                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Họ và tên <span className="text-red-500">*</span></label>
                  <input type="text" value={regData.name} onChange={(e) => setRegData({...regData, name: e.target.value})} placeholder="Nguyễn Văn A" className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-spa-gold focus:ring-1 focus:ring-spa-gold" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Dịch vụ quan tâm <span className="text-gray-400 font-normal lowercase">(Có thể chọn nhiều)</span></label>
                  <div className="grid grid-cols-1 gap-2 mt-2 max-h-[220px] overflow-y-auto pr-1">
                    {availableServices.map(svc => {
                      const isSelected = regData.selectedServices.includes(svc.id);
                      return (
                        <div key={svc.id} onClick={() => {
                          setRegData(prev => ({
                            ...prev, 
                            selectedServices: isSelected ? prev.selectedServices.filter(id => id !== svc.id) : [...prev.selectedServices, svc.id]
                          }));
                        }} className={`p-3 rounded-xl border text-sm cursor-pointer transition-all flex justify-between items-center select-none ${isSelected ? 'bg-spa-dark border-spa-dark shadow-md' : 'bg-white border-gray-200 hover:border-spa-gold/50'}`}>
                          <div className="flex items-center gap-2 overflow-hidden">
                            {isSelected ? <CheckCircle2 className="text-[#FDE08B] flex-shrink-0" size={18} fill="currentColor" /> : <div className="w-[18px] h-[18px] rounded-full border border-gray-300 flex-shrink-0"></div>}
                            <span className={`${isSelected ? 'font-semibold text-white' : 'font-medium text-gray-700'} truncate`}>{svc.name}</span>
                          </div>
                          <span className={`text-[11px] ${isSelected ? 'text-[#FDE08B]' : 'text-gray-500'} font-medium whitespace-nowrap ml-2`}>{svc.priceText}</span>
                        </div>
                      )
                    })}
                  </div>
                </div>
                <div className="flex gap-3 pt-4">
                  <button onClick={() => setClientScreen('welcome')} className="w-1/3 bg-gray-100 text-gray-700 py-3 rounded-xl text-sm font-medium hover:bg-gray-200 transition-all">Quay lại</button>
                  <button onClick={submitRegistration} className="w-2/3 bg-spa-dark text-white py-3 rounded-xl text-sm font-medium hover:bg-slate-800 transition-all shadow-md">Đăng ký & Check-in</button>
                </div>
              </div>
            </div>
          )}

          {/* MÀN HÌNH: THÀNH CÔNG */}
          {clientScreen === 'success' && successData && (
            <div className="bg-white/95 backdrop-blur-md p-6 md:p-8 fade-in min-h-[450px]">
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 text-green-500 mb-4 scale-in">
                  <CheckCircle2 size={40} />
                </div>
                <h2 className="text-gray-800 text-xl font-bold">Check-in Thành công</h2>
                <p className="text-green-700 text-sm mt-3 font-medium bg-green-50 p-3 rounded-lg border border-green-100 leading-relaxed">
                  Cảm ơn quý khách đã đến Viện thẩm mỹ Khothi, thông tin đã gửi đến nhân viên tư vấn.
                </p>
              </div>

              <div className="space-y-3 mt-6">
                <div className="flex justify-between items-center bg-gray-50 p-3.5 rounded-xl border border-gray-200 text-sm shadow-sm">
                  <div className="flex items-center gap-2">
                    <Clock className="text-spa-dark" size={18} />
                    <span className="text-gray-500 font-medium">Giờ Check-in:</span>
                    <span className="font-bold text-gray-800">{successData.time}</span>
                  </div>
                  <div className="h-5 w-px bg-gray-300"></div>
                  <div className="flex items-center gap-2">
                    <MapPin className="text-spa-dark" size={18} />
                    <span className="text-gray-500 font-medium">Cơ sở:</span>
                    <span className="font-bold text-gray-800 text-right">{successData.branch}</span>
                  </div>
                </div>

                <div className="border border-gray-100 rounded-xl p-4 bg-gray-50 relative overflow-hidden">
                  <div className="absolute left-0 top-0 bottom-0 w-1 bg-spa-dark"></div>
                  <div className="flex items-center gap-2 mb-3">
                    <User className="text-gray-400" size={18} />
                    <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wider">Thông tin Khách hàng</h3>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between"><span className="text-gray-500">Họ tên</span> <span className="font-semibold text-gray-800">{successData.name}</span></div>
                    <div className="flex justify-between"><span className="text-gray-500">SĐT</span> <span className="font-semibold text-gray-800">{successData.phone}</span></div>
                  </div>
                </div>

                <div className="border border-gray-100 rounded-xl p-4 bg-amber-50/50 relative overflow-hidden">
                  <div className="absolute left-0 top-0 bottom-0 w-1 bg-spa-gold"></div>
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles className="text-spa-gold" size={18} />
                    <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wider">Chi tiết Dịch vụ</h3>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between"><span className="text-gray-500">Dịch vụ</span> <span className="font-medium text-gray-800 text-right max-w-[150px] truncate" title={successData.service}>{successData.service}</span></div>
                    <div className="flex justify-between border-t border-gray-200 pt-2 mt-1"><span className="text-gray-500">Chi phí dự kiến</span> <span className="font-bold text-spa-dark">{successData.price}</span></div>
                  </div>
                </div>
              </div>
              <button onClick={() => { setPhone(''); setClientScreen('idle'); }} className="mt-8 w-full bg-gray-100 text-gray-700 py-3 rounded-xl text-sm font-medium hover:bg-gray-200 transition-all">
                Về trang chủ
              </button>
            </div>
          )}

          {/* Modal Xác nhận Đăng ký */}
          {showModal && (
            <div className="fixed inset-0 z-[60] flex items-center justify-center fade-in">
              <div className="absolute inset-0 bg-[#092C23]/70 backdrop-blur-sm" onClick={() => setShowModal(false)}></div>
              <div className="bg-white rounded-[24px] shadow-2xl w-11/12 max-w-sm md:max-w-md relative z-10 p-6 md:p-8 text-center border border-gray-100">
                <div className="w-16 h-16 md:w-20 md:h-20 bg-amber-50 text-spa-gold rounded-full flex items-center justify-center mx-auto mb-4 md:mb-5 shadow-sm border border-amber-100">
                  <CalendarPlus size={36} />
                </div>
                <h3 className="text-lg md:text-xl font-bold text-gray-800 mb-2 md:mb-3">Chưa có lịch hẹn</h3>
                <p className="text-gray-500 text-sm md:text-base mb-6 md:mb-8 leading-relaxed px-1 md:px-4">
                  Số điện thoại này chưa được đặt lịch trước. Quý khách vui lòng tạo lịch hẹn mới để chuyên viên hỗ trợ nhé.
                </p>
                <div className="flex flex-col-reverse sm:flex-row gap-3">
                  <button onClick={() => setShowModal(false)} className="w-full sm:w-1/2 py-3 md:py-3.5 rounded-xl bg-gray-100 text-gray-700 font-medium text-sm md:text-base hover:bg-gray-200 transition-colors">Hủy bỏ</button>
                  <button onClick={confirmRegister} className="w-full sm:w-1/2 py-3 md:py-3.5 rounded-xl bg-spa-dark text-white font-medium text-sm md:text-base hover:bg-slate-800 transition-all shadow-md flex items-center justify-center gap-2">Tạo lịch ngay <ArrowRight size={18}/></button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ĐÃ SỬA: Tương tự, đổi tên thành hàm render thông thường
  const renderAdminView = () => {
    const total = queueData.length;
    const waiting = queueData.filter(i => i.status === 'waiting').length;
    const served = queueData.filter(i => i.status === 'served').length;
    const newCustomers = queueData.filter(i => i.type === 'walkin').length;
    const dateOptions = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };

    const updateStatus = (id, newStatus) => {
      setQueueData(prev => prev.map(item => item.id === id ? { ...item, status: newStatus } : item));
    };

    return (
      <div className="flex-1 bg-slate-50 w-full min-h-screen flex flex-col fade-in">
        <div className="bg-spa-dark border-b border-[#C79F4B]/30 px-6 py-3 flex justify-between items-center sticky top-0 z-10 shadow-sm">
          <div className="flex items-center space-x-4">
            <div className="flex items-center gap-3 text-white">
              <img src="https://khothi-cms.buso.asia/images/logo-v.png" alt="Khothi" className="h-8 object-contain drop-shadow-sm rounded-[5px]" />
              <h1 className="text-sm font-bold tracking-widest uppercase border-l border-white/20 pl-3">Dashboard</h1>
            </div>
            <span className="flex items-center gap-1 text-[11px] bg-green-500/20 text-green-300 border border-green-500/30 px-2 py-1 rounded-full font-semibold uppercase tracking-wider">
              <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span><span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span></span>
              Trực tuyến
            </span>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-xs text-gray-300 flex items-center gap-2">
              <img src="https://ui-avatars.com/api/?name=Le+Tan&background=C79F4B&color=fff" alt="Avatar" className="w-7 h-7 rounded-full" />
              <div><p className="font-medium text-white">Lễ tân 01</p><p className="text-[10px]">Cơ sở Quận 1</p></div>
            </div>
          </div>
        </div>

        <div className="p-6 mx-auto w-full flex-1">
          <div className="flex justify-between items-end mb-6">
            <div>
              <h2 className="text-xl font-bold text-gray-800">Tổng quan ca làm việc</h2>
              <p className="text-sm text-gray-500 mt-1">{new Date().toLocaleDateString('vi-VN', dateOptions)}</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center"><Users size={24} /></div>
              <div><div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Tổng khách</div><div className="text-2xl font-bold text-gray-800">{total}</div></div>
            </div>
            <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-orange-50 text-orange-600 flex items-center justify-center"><Hourglass size={24} /></div>
              <div><div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Đang chờ</div><div className="text-2xl font-bold text-gray-800">{waiting}</div></div>
            </div>
            <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center"><CheckCircle2 size={24} /></div>
              <div><div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Đã phục vụ</div><div className="text-2xl font-bold text-gray-800">{served}</div></div>
            </div>
            <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center"><UserPlus size={24} /></div>
              <div><div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Khách mới</div><div className="text-2xl font-bold text-gray-800">{newCustomers}</div></div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden flex flex-col h-[500px]">
            <div className="px-5 py-4 border-b border-gray-200 flex justify-between items-center bg-gray-50/50">
              <div className="flex items-center gap-2"><List className="text-gray-500" size={18} /><h2 className="text-sm font-semibold text-gray-800">Hàng đợi Check-in hôm nay</h2></div>
              <button onClick={() => setQueueData([...queueData])} className="px-3 py-1.5 bg-white border border-gray-300 rounded-lg text-xs font-medium hover:bg-gray-50 flex items-center gap-1"><RefreshCw size={14} /> Làm mới</button>
            </div>
            
            <div className="overflow-auto flex-1 p-0 relative">
              {queueData.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 text-gray-400 h-full">
                  <ClipboardList size={48} className="mb-3 text-gray-300" />
                  <p className="text-sm">Chưa có khách hàng nào check-in.</p>
                </div>
              ) : (
                <table className="w-full text-left border-collapse whitespace-nowrap">
                  <thead className="sticky top-0 bg-gray-50 z-10 shadow-sm">
                    <tr className="text-xs text-gray-500 uppercase tracking-wider">
                      <th className="px-5 py-3 font-semibold">Thời gian</th><th className="px-5 py-3 font-semibold">Khách hàng</th>
                      <th className="px-5 py-3 font-semibold">Số điện thoại</th><th className="px-5 py-3 font-semibold">Dịch vụ quan tâm</th>
                      <th className="px-5 py-3 font-semibold">Loại</th><th className="px-5 py-3 font-semibold">Trạng thái</th><th className="px-5 py-3 font-semibold text-right">Thao tác</th>
                    </tr>
                  </thead>
                  <tbody className="text-sm text-gray-600 divide-y divide-gray-100">
                    {queueData.map(item => (
                      <tr key={item.id} className={`hover:bg-slate-50 transition-colors ${item.status === 'served' ? 'opacity-60 bg-gray-50' : ''}`}>
                        <td className="px-5 py-3 font-medium text-gray-500">{item.time}</td>
                        <td className="px-5 py-3 font-bold text-gray-800">{item.name}</td>
                        <td className="px-5 py-3">{item.phone}</td>
                        <td className="px-5 py-3 text-gray-600">{item.service}</td>
                        <td className="px-5 py-3">
                          {item.type === 'booked' ? <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-[10px] font-bold">Có hẹn</span>
                                                  : <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-[10px] font-bold">Khách mới</span>}
                        </td>
                        <td className="px-5 py-3">
                          {item.status === 'waiting' ? <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded text-[10px] font-bold flex items-center gap-1 w-max"><span className="w-1.5 h-1.5 rounded-full bg-orange-500 animate-pulse"></span> Đang chờ</span>
                                                     : <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-[10px] font-bold">Đã phục vụ</span>}
                        </td>
                        <td className="px-5 py-3 text-right">
                          {item.status === 'waiting' ? (
                            <button onClick={() => updateStatus(item.id, 'served')} className="px-3 py-1 text-[11px] font-semibold text-white bg-spa-dark rounded hover:bg-slate-800 transition-colors">Tiếp nhận</button>
                          ) : (<span className="text-xs text-gray-400 italic">Hoàn tất</span>)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="font-sans antialiased min-h-screen flex flex-col relative bg-slate-50">
      <CustomStyles />

      <div className="fixed top-5 right-5 z-[9999] flex flex-col gap-2">
        {toasts.map(toast => (
          <div key={toast.id} className={`bg-white border-l-4 shadow-md px-5 py-3 rounded-md text-sm text-gray-800 flex items-center gap-3 animate-toast ${toast.type === 'success' ? 'border-green-500' : 'border-red-500'}`}>
            {toast.type === 'success' ? <CheckCircle2 className="text-green-500" size={20} /> : <AlertCircle className="text-red-500" size={20} />}
            <span>{toast.message}</span>
          </div>
        ))}
      </div>

      <button onClick={() => {
        setCurrentView(v => v === 'client' ? 'admin' : 'client');
        if (currentView === 'admin') setClientScreen('idle');
      }} className="fixed bottom-4 right-4 z-50 bg-white border border-gray-200 shadow-lg rounded-full px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center gap-2 transition-all">
        <ArrowRightLeft size={16} />
        <span>{currentView === 'client' ? 'Chuyển sang Lễ tân' : 'Về màn hình Khách'}</span>
      </button>

      {/* ĐÃ SỬA: Gọi trực tiếp dưới dạng hàm để tránh render lại Component */}
      {currentView === 'client' ? renderClientView() : renderAdminView()}
    </div>
  );
}